import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ApiError } from '../../lib/api';
import type { PromotionPlan } from '../../types/promotionPlan';
import { createRolloutFromPlan } from './api';

// Right-column "Rollout" panel on the Plan detail page (Consolidation §3).
// Pre-Rollout: a single-click Create-Rollout button (NO modal — every
// Rollout field is read from the Plan server-side). Gated on ≥1 attached
// Campaign + both promo datetimes set, with a tooltip + inline reason for
// what's missing. Post-Rollout: the View-Rollout link.
export function PlanRolloutPanel({ plan }: { plan: PromotionPlan }) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const mut = useMutation({
    mutationFn: () => createRolloutFromPlan(plan.id),
    onSuccess: async ({ rollout }) => {
      await qc.invalidateQueries({ queryKey: ['plan', plan.id] });
      await qc.invalidateQueries({ queryKey: ['plans'] });
      await qc.invalidateQueries({ queryKey: ['rollouts'] });
      navigate(`/rollouts/${rollout.id}`);
    },
  });

  if (plan.rolloutId) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          This Plan is connected to a Rollout. The promo window, slug,
          month-year, default Campaign and notes are now edited on the
          Rollout.
        </p>
        <Link to={`/rollouts/${plan.rolloutId}`}>
          <Button type="button">View Rollout →</Button>
        </Link>
      </div>
    );
  }

  const hasCampaigns = plan.campaignIds.length > 0;
  const hasStart = !!plan.promoStartDate;
  const hasEnd = !!plan.promoEndDate;
  const canCreate = hasCampaigns && hasStart && hasEnd;

  const disabledReason = !hasCampaigns
    ? 'Attach at least one Campaign'
    : !hasStart
      ? 'Set promo start datetime'
      : !hasEnd
        ? 'Set promo end datetime'
        : '';

  const errMsg =
    mut.error instanceof ApiError
      ? mut.error.message
      : mut.error
        ? String(mut.error)
        : null;

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        Spawn the execution Rollout in one click. Every field is taken from
        this Plan — set them all before creating. Rows generate for every
        active location × the attached Campaigns.
      </p>
      <span title={disabledReason} className="inline-block">
        <Button
          type="button"
          disabled={!canCreate || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Creating…' : 'Create Rollout'}
        </Button>
      </span>
      {!canCreate && (
        <p className="text-xs text-slate-500">{disabledReason}.</p>
      )}
      {errMsg && <p className="text-sm text-red-600">{errMsg}</p>}
    </div>
  );
}
